import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import useUIStore from '@/app/store/ui.store';
import { useAuth } from '@/shared/hooks/useAuth';
import { useIsInterim } from '@/shared/hooks/useTenant';
import { useInboxCount } from '@/shared/hooks/useInboxCount';
import { useMissionsEndingSoon } from '@/features/hr/admin/shared/api';
import { Icon, Avatar } from '@/design-system/primitives';
import { NavGroup } from './NavGroup';
import { filterNav, type FilterContext } from './filterNav';
import { personaFromPath } from './personaFromPath';
import { navAgency } from './navAgency';
import { navManager } from './navManager';
import { navEmployee } from './navEmployee';
import type { NavItemConfig, Persona, PersonaNavConfig } from './types';

const CONFIG_BY_PERSONA: Record<Persona, PersonaNavConfig> = {
  agency: navAgency,
  manager: navManager,
  employee: navEmployee,
};

const PERSONA_HEADER: Record<Exclude<Persona, 'agency'>, { title: string; subtitle: string; tileBg: string }> = {
  manager: { title: 'Manager', subtitle: "Chef d'équipe", tileBg: 'oklch(0.65 0.16 80)' },
  employee: { title: 'Intérimaire', subtitle: 'Self-service', tileBg: 'oklch(0.58 0.15 150)' },
};

export const PersonaSidebar: React.FC = () => {
  const { isSidebarCollapsed } = useUIStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, permissions, isAdminRole, adminContext } = useAuth();
  const isInterim = useIsInterim();
  const { data: inboxCount } = useInboxCount();
  const { data: ending } = useMissionsEndingSoon(7);

  const persona = personaFromPath(location.pathname);
  const config = CONFIG_BY_PERSONA[persona];

  // Pure permission check closure — mirrors `usePermissionCheck` semantics but
  // operates directly on the already-loaded `permissions` object so it can be
  // called from a map/filter without violating the rules of hooks.
  const ctx: FilterContext = useMemo(() => {
    const hasPermission = (q: { module: string; feature: string }): boolean => {
      if (isAdminRole) return true;
      if (!permissions?.actions) return true; // optimistic, matches usePermissionCheck
      return permissions.actions.some(
        (a) => a.module === q.module && a.feature === q.feature && a.hasAccess,
      );
    };
    const hasModule = (m: string): boolean => {
      if (isAdminRole) return true;
      if (!permissions?.actions) return true;
      return permissions.actions.some((a) => a.module === m && a.hasAccess);
    };
    return {
      isInterim,
      isAdmin: adminContext?.isAdmin ?? false,
      hasPermission,
      hasModule,
    };
  }, [permissions, isAdminRole, adminContext, isInterim]);

  const groups = useMemo(() => filterNav(config.groups, ctx), [config, ctx]);

  const badgeFor = (source?: NavItemConfig['badgeSource']): number | undefined => {
    if (source === 'inbox') return inboxCount?.pendingApprovals ?? 0;
    if (source === 'missions-ending') return ending?.length ?? 0;
    return undefined;
  };

  const displayName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
    || user?.username
    || 'Utilisateur';

  const userRole = persona === 'agency'
    ? 'Administrateur'
    : persona === 'manager'
      ? "Chef d'équipe"
      : 'Collaborateur';

  const tenantName = (user?.tenantId ?? 'Tenant').replace(/^\w/, (c) => c.toUpperCase());
  const tenantInitials = tenantName.slice(0, 2).toUpperCase();

  return (
    <aside
      className={clsx(
        'h-screen sticky top-0 bg-surface border-r border-border flex flex-col transition-all duration-200 ease-out overflow-hidden',
        isSidebarCollapsed ? 'w-[64px]' : 'w-[244px]',
      )}
    >
      {!isSidebarCollapsed && (
        <div className="px-3 pt-3 pb-2 border-b border-border">
          {persona === 'agency' ? (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-surface-muted text-left"
            >
              <div
                className="w-9 h-9 rounded flex items-center justify-center text-xs font-semibold text-white shrink-0"
                style={{ background: 'oklch(0.62 0.17 30)' }}
              >
                {tenantInitials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text truncate">{tenantName}</div>
                <div className="text-[11px] text-text-mute truncate">Agence d'intérim</div>
              </div>
              <Icon name="ChevronDown" size={14} className="text-text-mute" />
            </button>
          ) : (
            <div className="w-full flex items-center gap-2 px-2 py-2 rounded">
              <div
                className="w-9 h-9 rounded flex items-center justify-center text-xs font-semibold text-white shrink-0"
                style={{ background: PERSONA_HEADER[persona].tileBg }}
              >
                <span className="w-3 h-3 bg-white/90 rounded-sm" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text truncate">
                  {PERSONA_HEADER[persona].title}
                </div>
                <div className="text-[11px] text-text-mute truncate">
                  {PERSONA_HEADER[persona].subtitle}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-3">
        {groups.map((group) => (
          <NavGroup
            key={group.key}
            group={group}
            pathname={location.pathname}
            collapsed={isSidebarCollapsed}
            getBadge={badgeFor}
          />
        ))}
      </nav>

      {!isSidebarCollapsed && (
        <div className="border-t border-border px-3 py-3 flex items-center gap-2">
          <Avatar name={displayName} size={32} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-text truncate">{displayName}</div>
            <div className="text-[11px] text-text-mute truncate">{userRole}</div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-surface-muted text-text-soft"
            aria-label="Paramètres"
          >
            <Icon name="Settings" size={16} />
          </button>
        </div>
      )}
    </aside>
  );
};

export default PersonaSidebar;
